<script>
/**
 * Which reasons belong under which outcome.
 *
 * The codes stay 'held-' while every display reads 'Withheld': a code is a stable identifier and
 * renaming it would orphan anything already written against it, where the word a nurse reads is
 * copy. Withheld is also the accurate word - it is a completed act on one occurrence, against the
 * order-level hold which is an ongoing state of the order - and it carries the right warning,
 * because the button is terminal. A nurse meaning to give the dose an hour late leaves it open
 * rather than pressing anything here.
 */
/**
 * Why the patient refused, which is what a later reader is actually asking - the status already
 * says that they did. 'patient-refused' stays as the generic and first option, because a code is a
 * stable identifier and every refusal recorded before these existed carries it.
 */
export const REFUSED_REASONS = ['patient-refused', 'refused-side-effects', 'refused-does-not-want', 'refused-asleep', 'refused-took-own', 'refused-no-reason-given'];

export const WITHHELD_REASONS = ['held-clinical-judgment', 'held-pending-assessment', 'held-vitals-outside-parameters'];

/**
 * Why a dose nobody gave was never given. Disjoint from the refusal and withholding codes on
 * purpose: those are acts, and this is a lapse - a chart that cannot tell them apart cannot answer
 * the only question anyone asks of a missed dose, which is why it was missed.
 */
export const MISSED_REASONS = ['patient-not-present', 'patient-off-site', 'patient-discharged', 'medication-unavailable', 'other'];

const ADMINISTERED = { label: 'Administered', color: 'success', icon: 'check' };

const OUTCOME_GROUPS = [
    { codes: WITHHELD_REASONS, display: { label: 'Withheld', color: 'warning', icon: 'circle-pause' } },
    { codes: REFUSED_REASONS, display: { label: 'Refused', color: 'error', icon: 'xmark' } },
    { codes: MISSED_REASONS, display: { label: 'Missed', color: 'error', icon: 'circle-minus' } },
];

/**
 * What a reader calls this dose - the reason's GROUP, never the bare FHIR status.
 *
 * Held, refused and patient-not-present all write 'not-done'. A single 'Not given' chip loses
 * exactly the distinction every reader of a history or a report came for, so the taxonomy that
 * writes the reason is what reads it back.
 *
 * @param {object} administration
 * @returns {{label: string, color: string, icon: string}}
 */
export const outcomeOf = (administration) => {
    if (administration?.status === 'completed') return ADMINISTERED;
    const code = administration?.statusReason?.[0]?.coding?.[0]?.code;
    return OUTCOME_GROUPS.find((group) => group.codes.includes(code))?.display ?? { label: 'Not given', color: 'error', icon: 'ban' };
};

/**
 * A recorded reason in words.
 *
 * A record keeps only the code; the display lives on the Concept, which is why this resolves
 * against the same terminology the picker writes from rather than against the record. Without it
 * a reason renders as a slug, or as nothing at all.
 *
 * @param {object} concept - a CodeableConcept off the record
 * @param {Array<object>} known - Concepts loaded from the reason, lateness and shortfall systems
 * @returns {string}
 */
export const reasonTextOf = (concept, known) => {
    if (concept?.text) return concept.text;
    const coding = concept?.coding?.[0];
    if (!coding) return '';
    return coding.display || (known || []).find((item) => item?.code === coding.code)?.display || coding.code || '';
};
</script>

<script setup>
import { computed } from 'vue';
import { STATUS_REASON_SYSTEM, SYSTEM_ONLY_STATUS_REASON } from '@/composables/useMedicationAdministration';
import { useTerminologyList } from '@/composables/useTerminologyList';

/**
 * Why a dose was not given as scheduled.
 *
 * A combobox and not a select: the concept list is a starting point, and it comes back empty
 * whenever the terminology migration has not been applied to the box — which must not leave a
 * nurse unable to explain a dose.
 */
const props = defineProps({
    // Accepts a bare code seeded off a record; always emits {code, display} or {text}.
    modelValue: {
        type: [String, Object],
        default: null,
    },
    label: {
        type: String,
        default: 'Reason',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    dataCy: {
        type: String,
        default: 'medPassReason',
    },
    // The codes this picker offers. Empty means the whole list, which is what the day review
    // wants — it answers for a dose whatever the outcome turned out to be.
    codes: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['update:modelValue']);

// Said in the label rather than marked beside it: the asterisk sits outside the field, where a
// combobox in a dense row leaves it reading as though it belonged to the control before it.
const fieldLabel = computed(() => (props.required ? `${props.label} (required)` : props.label));

// The close dialog renders a picker per unanswered dose; the expansion is cached, so they share one request.
const { items: concepts } = useTerminologyList({ systems: [STATUS_REASON_SYSTEM] }, { sort: 'code' });

const selected = computed(() => {
    const value = props.modelValue;
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value.code ?? value.text ?? null;
});

// 'not-covered' is written only by the day close. The write layer rejects it too — both are
// needed, because a machine noting that nobody came must never read as a clinical judgment.
const items = computed(() =>
    concepts.value
        .filter((concept) => concept?.code !== SYSTEM_ONLY_STATUS_REASON)
        .filter((concept) => !props.codes.length || props.codes.includes(concept?.code))
        .map((concept) => ({ value: concept.code, title: concept.display || concept.code }))
        .sort((a, b) => a.title.localeCompare(b.title)),
);
// Free text coded against the reason system would invent a member of a governed value set, and
// outcomeOf() reads that set to tell a refusal from a withholding from a lapse.
const onPicked = (value) => {
    const typed = value === null || value === undefined ? '' : String(value).trim();
    if (!typed) return emit('update:modelValue', null);
    const match = items.value.find((item) => item.value === typed);
    emit('update:modelValue', match ? { code: match.value, display: match.title } : { text: typed });
};
</script>

<template>
    <div class="d-flex align-center ga-2">
        <v-combobox
            :model-value="selected"
            :items="items"
            :return-object="false"
            :label="fieldLabel"
            :disabled="props.disabled"
            density="comfortable"
            hide-details="auto"
            class="flex-grow-1"
            :data-cy="props.dataCy"
            @update:model-value="onPicked"
        />
    </div>
</template>
